/**
 * InlineNotice — Agency Portal v2 Slice 4 (§4.0).
 *
 * One banner shape for every outcome on the agency surface. Pinned
 * copy / colour discipline:
 *
 *   success | lime border  | role=status | Saved · Sent
 *   error   | coral border | role=alert  | Any failure, directly above the action
 *   warn    | amber border | role=alert  | Partial import, budget low
 *   info    | sky border   | role=status | Neutral notes
 *
 * Keep the API narrow: tone + title + optional body. Rendered inline;
 * errors live directly above the button that caused them (never at the
 * bottom of a scrollable modal where SendDialog hid them before).
 */

import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, Check, Info } from 'lucide-react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";

export type NoticeTone = 'success' | 'error' | 'warn' | 'info';

export interface InlineNoticeProps {
  readonly tone: NoticeTone;
  readonly title?: ReactNode;
  readonly children?: ReactNode;
  readonly 'data-testid'?: string;
}

export function InlineNotice({ tone, title, children, ...rest }: InlineNoticeProps): JSX.Element {
  const C = useC();
  const borderMap = { success: C.lime, error: C.coral, warn: C.amber, info: C.sky } as const;
  const iconMap = { success: Check, error: AlertCircle, warn: AlertTriangle, info: Info } as const;
  const Icon = iconMap[tone];
  const borderL = borderMap[tone];
  const role = tone === 'error' || tone === 'warn' ? 'alert' : 'status';

  return (
    <div
      role={role}
      data-testid={rest['data-testid']}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${borderL}`,
        borderRadius: 2,
        fontFamily: sansFont,
        fontSize: 13,
        lineHeight: 1.45,
        color: C.text,
      }}
    >
      <span style={{ flex: '0 0 auto', marginTop: 1 }}>
        <Icon size={14} color={borderL} />
      </span>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        {title ? <div style={{ fontWeight: 500, marginBottom: children ? 2 : 0 }}>{title}</div> : null}
        {children ? <div style={{ color: C.muted }}>{children}</div> : null}
      </div>
    </div>
  );
}
